import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Paperclip,
  Mic,
  MoreVertical,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Check,
  MessageSquare,
  Plus,
  Clock,
  X,
  Loader2,
  Bot,
  User,
  Trash2,
  Download,
  Settings,
  Info,
  Edit3,
  CornerUpLeft,
  Volume2,
  VolumeX,
  Users,
  Menu,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  Calendar,
  Image as ImageIcon,
  Facebook,
  Instagram,
  Linkedin,
} from 'lucide-react';

// Custom X (Twitter) icon component
const XTwitterIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
import { getEmployeeById, AI_EMPLOYEES, CAPABILITY_DETAILS } from '../data/employees';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useWorkspaceStore } from '../store/workspace';
import { useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { ConnectorActionCard, ConnectorEditModal, ConnectorResultCard } from '../components/chat';
import type { ConnectorProvider, ConnectorAction } from '../types';

// Web Speech API Types for voice input
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Get user's preferred hour format from localStorage (shared with DateTimePicker)
const getPreferredHourFormat = (): '12' | '24' => {
  const saved = localStorage.getItem('preferredHourFormat');
  if (saved === '12' || saved === '24') return saved;
  // Try to detect from system locale
  const testDate = new Date(2024, 0, 1, 13, 0);
  const formatted = testDate.toLocaleTimeString();
  if (formatted.toLowerCase().includes('pm') || formatted.toLowerCase().includes('am')) {
    return '12';
  }
  return '24';
};

// Format time based on user preference
const formatTime = (date: Date): string => {
  const format = getPreferredHourFormat();
  if (format === '12') {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

interface GeneratedImage {
  url: string;
  style?: string;
  revised_prompt?: string;
}

interface RouteSuggestion {
  employeeId: string;
  employeeName: string;
  employeeRole: string;
}

interface CollaborationResult {
  employeeId: string;
  employeeName: string;
  employeeRole: string;
  content: string;
}

interface PresentationSlide {
  number: number;
  title: string;
  content: string[];
  notes?: string;
  animation?: string;
  transition?: string;
  layout?: string;
}

interface PresentationData {
  id: string;
  title: string;
  subtitle?: string;
  author?: string;
  theme: string;
  style: string;
  slides: PresentationSlide[];
  metadata?: {
    totalSlides: number;
    createdAt: string;
    topic: string;
  };
}

interface GeneratedPresentation {
  id: string;
  data: PresentationData;
  credits: number;
}

// Connector types for integration actions
interface PendingConnectorAction {
  id: number;
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  preview: Record<string, unknown>;
  actionData: Record<string, unknown>;
  requiresApproval: boolean;
  status?: 'pending' | 'approved' | 'rejected' | 'executed' | 'failed';
  result?: { success: boolean; data?: unknown; error?: string };
}

interface ConnectorResult {
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  success: boolean;
  data?: unknown;
  error?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokens_used?: number;
  images?: GeneratedImage[];
  imageUrl?: string; // For uploaded images
  routeSuggestions?: RouteSuggestion[]; // For Agent's routing suggestions
  collaborations?: CollaborationResult[]; // For cross-employee collaboration results
  presentations?: GeneratedPresentation[]; // For Slides employee presentations
  pendingActions?: PendingConnectorAction[]; // For connector write actions awaiting approval
  connectorResults?: ConnectorResult[]; // For connector read action results
}

interface Conversation {
  id: number;
  employee_id: string;
  title: string;
  last_message_at: string;
  message_count: number;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface ConversationsResponse {
  items: Conversation[];
  total: number;
}

interface MessageFromAPI {
  id: number;
  role: string;
  content: string;
  created_at: string;
  tokens_used?: number;
}

export default function EmployeeChat() {
  const { employeeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, updateUser, updateCredits } = useAuthStore();
  const { theme } = useThemeStore();
  const { currentWorkspace } = useWorkspaceStore();
  const isDark = theme === 'dark';
  const employee = getEmployeeById(employeeId || '');

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showUseCases, setShowUseCases] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true); // Default to true on desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // For minimized state
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  // Keep track of the last uploaded image for the conversation (for follow-up edits)
  const [conversationImage, setConversationImage] = useState<string | null>(null);

  // Helper functions for localStorage image persistence
  const getConversationImageKey = (convId: number | null, empId: string) =>
    `sweetpilot_conv_image_${empId}_${convId || 'new'}`;

  const saveConversationImageToStorage = (convId: number | null, empId: string, image: string | null) => {
    const key = getConversationImageKey(convId, empId);
    if (image) {
      try {
        localStorage.setItem(key, image);
      } catch (e) {
        // localStorage might be full, try to clear old images
        console.warn('Failed to save image to localStorage:', e);
        clearOldConversationImages();
        try {
          localStorage.setItem(key, image);
        } catch {
          console.error('Still failed to save image after cleanup');
        }
      }
    } else {
      localStorage.removeItem(key);
    }
  };

  const loadConversationImageFromStorage = (convId: number | null, empId: string): string | null => {
    const key = getConversationImageKey(convId, empId);
    return localStorage.getItem(key);
  };

  const clearOldConversationImages = () => {
    // Clear conversation images older than 24 hours or if there are too many
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('sweetpilot_conv_image_')) {
        keysToRemove.push(key);
      }
    }
    // Keep only the 10 most recent (we can't know age, so just limit count)
    if (keysToRemove.length > 10) {
      keysToRemove.slice(0, keysToRemove.length - 10).forEach(key => {
        localStorage.removeItem(key);
      });
    }
  };
  const [showImagePreview, setShowImagePreview] = useState<string | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [isNewConversation, setIsNewConversation] = useState(false);
  const [collaboratingWith, setCollaboratingWith] = useState<string | null>(null);
  const [routedFrom, setRoutedFrom] = useState<string | null>(null);
  const [pendingRoutedMessage, setPendingRoutedMessage] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  // Mobile view state: 'list' = chat history, 'chat' = main chat, 'schedule' = post scheduling
  const [mobileView, setMobileView] = useState<'list' | 'chat' | 'schedule'>('chat');

  // Stored presentations state (for Slides employee)
  const [storedPresentations, setStoredPresentations] = useState<Array<{
    id: string;
    title: string;
    data: PresentationData;
    stored_at: string;
  }>>([]);
  const [showStoredSection, setShowStoredSection] = useState(false);
  const [promptIdeas, setPromptIdeas] = useState<Array<{
    id: number;
    idea_title: string;
    idea_description: string;
    suggested_prompt: string;
    idea_category: string;
  }>>([]);
  const [showIdeasSection, setShowIdeasSection] = useState(false);
  const [showMorePromptsModal, setShowMorePromptsModal] = useState(false);

  // Bot Settings Modal state
  const [showBotSettingsModal, setShowBotSettingsModal] = useState(false);
  const [botConfig, setBotConfig] = useState<{
    custom_instructions: string;
    tone: string;
  }>({ custom_instructions: '', tone: 'professional' });
  const [isSavingBotConfig, setIsSavingBotConfig] = useState(false);
  const [isLoadingBotConfig, setIsLoadingBotConfig] = useState(false);

  // Connector state
  const [editingAction, setEditingAction] = useState<PendingConnectorAction | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  // Social Media Post Creation state (for autopost from Social Media Manager)
  const [isAutoPostMode, setIsAutoPostMode] = useState(false);
  const [showPostPreviewSidebar, setShowPostPreviewSidebar] = useState(false);
  const [postDraft, setPostDraft] = useState<{
    content: string;
    imageUrl: string;
    publishDate: string;
    publishTime: string;
    contentType: 'post' | 'story' | 'reel';
    platform: string;
  }>({
    content: '',
    imageUrl: '',
    publishDate: '',
    publishTime: '17:00',
    contentType: 'post',
    platform: 'instagram',
  });
  const [isSchedulingPost, setIsSchedulingPost] = useState(false);
  const [connectedIntegrations, setConnectedIntegrations] = useState<Array<{
    id: number;
    provider: string;
    provider_name: string;
  }>>([]);
  const autoPostProcessedRef = useRef<boolean>(false);
  const isAutoPostModeRef = useRef<boolean>(false); // Ref for reliable state access in async functions

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const routedMessageProcessedRef = useRef<boolean>(false);
  const directMessageProcessedRef = useRef<boolean>(false);
  const handleSendRef = useRef<((routedContent?: string) => Promise<void>) | null>(null);

  // Voice recognition state
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // User scroll control - track if user has scrolled up during streaming
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const isNearBottomRef = useRef(true);

  // Detect mobile and set initial sidebar state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On mobile, hide sidebar by default and show chat view. On desktop, keep sidebar open.
      if (mobile) {
        setShowSidebar(false);
        // Keep mobileView as 'chat' by default on mobile
      } else {
        setShowSidebar(true);
        // Reset to chat view when switching to desktop
        setMobileView('chat');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize voice recognition
  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setVoiceSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update input with transcript
        if (finalTranscript) {
          setInput(prev => {
            const newValue = prev + (prev ? ' ' : '') + finalTranscript;
            return newValue;
          });
        }
      };

      recognition.onerror = (event: Event) => {
        console.error('Speech recognition error:', event);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Handle voice input toggle
  const handleVoiceInput = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start voice recognition:', error);
      }
    }
  }, [isListening]);

  // Scroll detection - track if user has scrolled up during streaming
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Consider "near bottom" if within 100px
      const isNearBottom = distanceFromBottom < 100;
      isNearBottomRef.current = isNearBottom;

      // If user is streaming and scrolls up, set flag to disable auto-scroll
      if (streamingContent && !isNearBottom) {
        setUserHasScrolledUp(true);
      }

      // If user scrolls back to bottom, re-enable auto-scroll
      if (isNearBottom) {
        setUserHasScrolledUp(false);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [streamingContent]);

  // Close employee dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target as Node)) {
        setShowEmployeeDropdown(false);
      }
    };
    if (showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmployeeDropdown]);

  // Auto-scroll to bottom - throttled during streaming to prevent shakiness
  const lastScrollTimeRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStreamingLengthRef = useRef<number>(0);

  useEffect(() => {
    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
      messagesEndRef.current?.scrollIntoView({ behavior });
    };

    // For new messages (not during streaming), scroll smoothly
    // Reset scroll flag when streaming ends
    if (messages.length > 0 && !streamingContent) {
      // Only auto-scroll if user hasn't scrolled up
      if (!userHasScrolledUp) {
        scrollToBottom('smooth');
      }
      prevStreamingLengthRef.current = 0;
      // Reset the scroll flag when streaming completes
      setUserHasScrolledUp(false);
      return;
    }

    // For streaming content, throttle scrolling to reduce jitter
    // But respect if user has manually scrolled up
    if (streamingContent && !userHasScrolledUp) {
      const now = Date.now();
      const contentGrowth = streamingContent.length - prevStreamingLengthRef.current;

      // Only scroll if content grew significantly or enough time passed
      if (contentGrowth > 50 || now - lastScrollTimeRef.current > 500) {
        lastScrollTimeRef.current = now;
        prevStreamingLengthRef.current = streamingContent.length;
        // Use instant scroll during active streaming to prevent animation conflicts
        scrollToBottom('instant');
      }

      // Clear any pending timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Schedule a smooth scroll when streaming settles
      scrollTimeoutRef.current = setTimeout(() => {
        if (!userHasScrolledUp) {
          scrollToBottom('smooth');
        }
      }, 200);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, streamingContent, userHasScrolledUp]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 128) + 'px';
    }
  }, [input]);

  // Reset all state when employee changes (switching via dropdown)
  useEffect(() => {
    // Reset routing state
    routedMessageProcessedRef.current = false;
    directMessageProcessedRef.current = false;
    setPendingRoutedMessage(null);
    setRoutedFrom(null);

    // Reset chat state - clear current conversation and messages
    setCurrentConversationId(null);
    setMessages([]);
    setStreamingContent('');
    setIsLoading(false);
    setInput('');
    setUploadedImage(null);
    setConversationImage(null);

    // Reset collaboration state
    setCollaboratingWith(null);

    // Clear conversations list (will be re-fetched by next useEffect)
    setConversations([]);
  }, [employeeId]);

  // Load conversations when employee changes or on mount
  useEffect(() => {
    if (token && employeeId) {
      loadConversations();
    }
  }, [token, employeeId]);

  // Load stored presentations and ideas for Slides employee
  useEffect(() => {
    if (token && employeeId === 'slides') {
      loadStoredPresentations();
      loadPromptIdeas();
    }
  }, [token, employeeId]);

  // Handle conversation from URL param
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && token) {
      const convIdNum = parseInt(convId);
      setCurrentConversationId(convIdNum);
      // Don't load messages if we just created a new conversation
      // (the first message is already in local state and being sent)
      if (isNewConversation) {
        setIsNewConversation(false);
      } else {
        loadMessages(convIdNum);
      }
      // Load conversation image from localStorage (for page reload persistence)
      const storedImage = loadConversationImageFromStorage(convIdNum, employeeId || '');
      if (storedImage) {
        setConversationImage(storedImage);
      }
    }
  }, [searchParams, token]);

  // Handle routed messages from other employees (e.g., Agent routing to Code)
  // Step 1: Detect routed message params and store them
  useEffect(() => {
    const isRouted = searchParams.get('routed') === 'true';
    const routedMessage = searchParams.get('message');
    const fromEmployee = searchParams.get('from');

    // Only process if this is a routed message, we have all params,
    // we haven't processed it yet, and we're on a different employee than the source
    if (isRouted && routedMessage && fromEmployee &&
        !routedMessageProcessedRef.current && token &&
        employeeId && fromEmployee !== employeeId) {

      routedMessageProcessedRef.current = true;

      // Store where the message came from for context display
      const sourceEmployee = getEmployeeById(fromEmployee);
      if (sourceEmployee) {
        setRoutedFrom(sourceEmployee.name);
      }

      // Store the message to send - decode it since it was URL encoded
      setPendingRoutedMessage(decodeURIComponent(routedMessage));

      // Clear the routed params from URL to prevent re-processing on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('routed');
      newParams.delete('message');
      newParams.delete('from');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, token, setSearchParams, employeeId]);

  // Handle direct messages from Dashboard (when message param is present but NOT routed)
  // This handles the intelligent routing from the main dashboard input
  useEffect(() => {
    const isRouted = searchParams.get('routed') === 'true';
    const directMessage = searchParams.get('message');
    const isAutoPost = searchParams.get('autopost') === 'true';

    // Only process direct messages (not routed ones, and not autopost)
    // Direct messages come from the Dashboard's intelligent routing
    if (!isRouted && !isAutoPost && directMessage &&
        !directMessageProcessedRef.current && token && employeeId && employee) {

      directMessageProcessedRef.current = true;

      // Decode the message
      const decodedMessage = decodeURIComponent(directMessage);

      // Clear the message param from URL immediately to prevent re-processing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('message');
      setSearchParams(newParams, { replace: true });

      // Queue the message in the input field instead of auto-sending
      // This allows the user to review and optionally edit the message before sending
      setTimeout(() => {
        setInput(decodedMessage);
        inputRef.current?.focus();
      }, 100);
    }
  }, [searchParams, token, setSearchParams, employeeId, employee]);

  // Handle direct message send from Dashboard routing
  const handleDirectMessageSend = async (message: string) => {
    if (!message || isLoading || !token) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setShowUseCases(false);
    setStreamingContent('');

    try {
      // Create conversation
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: message }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      if (reader) {
        await processStreamResponse(reader, message, userMessage);
      }
    } catch (error) {
      console.error('Failed to send direct message:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  };

  // Handle autopost mode from Social Media Manager
  useEffect(() => {
    const isAutoPost = searchParams.get('autopost') === 'true';

    if (isAutoPost && !autoPostProcessedRef.current && token && employeeId) {
      autoPostProcessedRef.current = true;
      setIsAutoPostMode(true);
      isAutoPostModeRef.current = true; // Also update ref for reliable access in async functions

      // Set default publish date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];
      setPostDraft(prev => ({ ...prev, publishDate: dateStr }));

      // Load connected social media integrations
      loadSocialMediaIntegrations();

      // Clear the autopost param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('autopost');
      setSearchParams(newParams, { replace: true });

      // Auto-send the message after a short delay
      setTimeout(() => {
        const autoMessage = `Hey ${employee?.name}, I need help creating a new social media post. Please generate an engaging post with an image and caption.`;
        handleAutoPostSend(autoMessage);
      }, 500);
    }
  }, [searchParams, token, setSearchParams, employeeId, employee]);

  // Handle image from Dashboard (when hasImage param is present)
  useEffect(() => {
    const hasImage = searchParams.get('hasImage') === 'true';
    const hasMessage = !!searchParams.get('message');

    if (hasImage && token && employeeId === 'pixel') {
      // Get the pending image from sessionStorage
      const pendingImage = sessionStorage.getItem('pendingImage');

      if (pendingImage) {
        // Set the uploaded image for preview
        setUploadedImage(pendingImage);
        // Also store as conversation image for follow-up edit requests
        setConversationImage(pendingImage);
        // Save to localStorage for persistence
        saveConversationImageToStorage(currentConversationId, employeeId || '', pendingImage);

        // Clear from sessionStorage
        sessionStorage.removeItem('pendingImage');
        sessionStorage.removeItem('pendingImageName');

        // If no message was provided (image-only upload from dashboard),
        // show a local Pixel message asking what to do with the image
        if (!hasMessage) {
          setShowUseCases(false);
          setMessages((prev) => [...prev, {
            id: `pixel-image-prompt-${Date.now()}`,
            role: 'assistant' as const,
            content: "I can see you've uploaded an image! What would you like me to do with it?\n\n- **Edit or enhance** the image\n- **Use it as a reference** to create a new design\n- **Remove or change the background**\n- **Apply a style transfer** to transform it\n\nJust tell me what you'd like and I'll get to work!",
            timestamp: new Date(),
          }]);
        }
      }

      // Clear the hasImage param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('hasImage');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, token, setSearchParams, employeeId]);

  // Reset autopost mode when employee changes
  useEffect(() => {
    autoPostProcessedRef.current = false;
    setIsAutoPostMode(false);
    isAutoPostModeRef.current = false; // Also reset ref
    setShowPostPreviewSidebar(false);
  }, [employeeId]);

  // Load social media integrations for post scheduling
  const loadSocialMediaIntegrations = async () => {
    try {
      const response = await fetch(`${API_URL}/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      interface IntegrationsResponse {
        success: boolean;
        data?: Array<{
          id: number;
          provider: string;
          provider_name: string;
          status: string;
        }>;
      }
      const data = (await response.json()) as IntegrationsResponse;
      if (data.success && data.data) {
        const socialIntegrations = data.data.filter(
          (i) => ['facebook', 'instagram', 'linkedin', 'twitter'].includes(i.provider) && i.status === 'connected'
        );
        setConnectedIntegrations(socialIntegrations);
        if (socialIntegrations.length > 0) {
          setPostDraft(prev => ({ ...prev, platform: socialIntegrations[0].provider }));
        }
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    }
  };

  // Handle autopost message send
  const handleAutoPostSend = async (message: string) => {
    if (!message || isLoading || !token) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setShowUseCases(false);
    setStreamingContent('');

    try {
      // Create conversation
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: message }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      if (reader) {
        await processStreamResponseWithPostExtraction(reader, message, userMessage);
      }
    } catch (error) {
      console.error('Failed to send autopost message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Process streaming response and extract post content for sidebar
  const processStreamResponseWithPostExtraction = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    _messageContent: string,
    _userMessage: Message
  ) => {
    const decoder = new TextDecoder();
    let fullContent = '';
    const generatedImages: Array<{ url: string; style?: string }> = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      const lines = text.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            // Streaming complete
            let cleanedContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '').trim();

            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: cleanedContent,
                timestamp: new Date(),
                images: generatedImages.length > 0 ? generatedImages : undefined,
              },
            ]);
            setStreamingContent('');

            // Extract content for post preview sidebar for autopost mode
            // This is the same logic as the normal Aria flow but triggered from autopost redirect
            // We check both the ref AND the content to ensure sidebar shows
            {
              // Extract caption - same cleaning logic as the normal Aria flow
              let postContent = cleanedContent;

              // Remove "Reading from Brain AI" and related messages
              postContent = postContent.replace(/📖\s*\*?Reading from Brain AI\.\.\.?\*?\s*/gi, '').trim();
              postContent = postContent.replace(/I'm reviewing your brand and business information to create personalized content:?\s*/gi, '').trim();
              postContent = postContent.replace(/---\s*/g, '').trim();
              postContent = postContent.replace(/✅\s*Brand knowledge loaded successfully\.?\s*/gi, '').trim();
              postContent = postContent.replace(/\*Generating social media graphic\.\.\.?\*/gi, '').trim();

              // Remove "Caption:" prefix if present
              postContent = postContent.replace(/^Caption:\s*/i, '').trim();

              // Remove bold formatting, markdown links, code blocks
              postContent = postContent
                .replace(/\*\*.*?\*\*/g, '')
                .replace(/\[.*?\]\(.*?\)/g, '')
                .replace(/```[\s\S]*?```/g, '')
                .trim();

              // Check if content has hashtags (indicates social media post)
              const hasHashtags = postContent.includes('#');

              // Show sidebar if: autopost mode OR (is Aria and has hashtags)
              if ((isAutoPostModeRef.current || hasHashtags) && postContent.length > 10) {
                // Use the generated image if available
                const imageUrl = generatedImages.length > 0 ? generatedImages[0].url : '';

                // If caption is too long, try to extract a shorter portion
                if (postContent.length > 500) {
                  const quotedMatch = postContent.match(/"([^"]+)"/);
                  if (quotedMatch) {
                    postContent = quotedMatch[1];
                  } else {
                    postContent = postContent.substring(0, 500) + '...';
                  }
                }

                // Set default publish date to tomorrow at 5 PM
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const publishDate = tomorrow.toISOString().split('T')[0];

                setPostDraft(prev => ({
                  ...prev,
                  content: postContent,
                  imageUrl: imageUrl,
                  publishDate: publishDate,
                  publishTime: '17:00',
                }));

                // Load integrations for the sidebar (needed for platform selection)
                loadSocialMediaIntegrations();

                // Show the post preview sidebar
                setShowPostPreviewSidebar(true);
                // On mobile, switch to schedule view
                if (window.innerWidth < 768) {
                  setMobileView('schedule');
                }

                // Reset the ref after showing sidebar (one-time trigger)
                isAutoPostModeRef.current = false;
              }
            }
          } else {
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                let displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '');
                displayContent = displayContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '');
                setStreamingContent(displayContent.trim());
              }
              if (parsed.generated_image) {
                generatedImages.push({
                  url: parsed.generated_image,
                  style: parsed.image_type || undefined,
                });
                // Also update postDraft if the sidebar is visible (image arrived after [DONE])
                if (showPostPreviewSidebar) {
                  setPostDraft(prev => ({ ...prev, imageUrl: parsed.generated_image }));
                }
              }
              if (parsed.done && typeof parsed.credits_remaining === 'number') {
                updateCredits(parsed.credits_remaining);
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
    }

    // Reload conversations
    loadConversations();
  };

  // Schedule the post
  const handleSchedulePost = async () => {
    if (!postDraft.content || !postDraft.publishDate || !token || !postDraft.platform) return;

    setIsSchedulingPost(true);
    try {
      // Format scheduled_at as local time string (YYYY-MM-DD HH:MM:SS) for proper timezone handling
      const year = parseInt(postDraft.publishDate.split('-')[0]);
      const month = parseInt(postDraft.publishDate.split('-')[1]);
      const day = parseInt(postDraft.publishDate.split('-')[2]);
      const [hours, minutes] = postDraft.publishTime.split(':').map(Number);
      const scheduledAtString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

      // Get user's timezone for proper scheduling
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(`${API_URL}/scheduled-posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: postDraft.content,
          image_url: postDraft.imageUrl || null,
          scheduled_at: scheduledAtString, // Local time format for consistency
          timezone: userTimezone, // Include timezone for accurate scheduling
          target_platforms: [postDraft.platform], // API expects array
          content_type: postDraft.contentType,
          status: 'posting', // Auto-approved when scheduled from chat - 'posting' means ready to post
          workspace_id: currentWorkspace?.id || null, // Include workspace for isolation
        }),
      });

      interface ScheduleResponse {
        success: boolean;
        error?: string;
      }
      const data = (await response.json()) as ScheduleResponse;
      if (data.success) {
        // Close sidebar and navigate to Social Media Manager
        setShowPostPreviewSidebar(false);
        navigate('/dashboard/social-media-manager');
      } else {
        console.error('Failed to schedule post:', data.error);
        alert(`Failed to schedule post: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to schedule post:', error);
      alert('Failed to schedule post. Please try again.');
    } finally {
      setIsSchedulingPost(false);
    }
  };

  // Regenerate caption
  const handleRegenerateCaption = async () => {
    if (!token || !postDraft.content) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/scheduled-posts/regenerate-caption`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_content: postDraft.content,
          platform: postDraft.platform,
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      interface RegenerateResponse {
        success: boolean;
        data?: { content: string };
        error?: string;
      }
      const data = (await response.json()) as RegenerateResponse;
      if (data.success && data.data) {
        setPostDraft(prev => ({ ...prev, content: data.data!.content }));
      } else if (data.error) {
        console.error('Failed to regenerate caption:', data.error);
      }
    } catch (error) {
      console.error('Failed to regenerate caption:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Regenerate image
  const handleRegenerateImage = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/scheduled-posts/regenerate-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: postDraft.content,
          platform: postDraft.platform,
        }),
      });

      interface RegenerateImageResponse {
        success: boolean;
        data?: { image_url: string };
      }
      const data = (await response.json()) as RegenerateImageResponse;
      if (data.success && data.data) {
        setPostDraft(prev => ({ ...prev, imageUrl: data.data!.image_url }));
      }
    } catch (error) {
      console.error('Failed to regenerate image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get provider icon for sidebar
  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'facebook':
        return <Facebook className="w-4 h-4" />;
      case 'instagram':
        return <Instagram className="w-4 h-4" />;
      case 'linkedin':
        return <Linkedin className="w-4 h-4" />;
      case 'twitter':
        return <XTwitterIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Get provider color
  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'facebook':
        return 'bg-blue-600';
      case 'instagram':
        return 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400';
      case 'linkedin':
        return 'bg-blue-700';
      case 'twitter':
        return 'bg-black';
      default:
        return 'bg-gray-500';
    }
  };

  const loadConversations = async () => {
    try {
      const workspaceParam = currentWorkspace?.id ? `&workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/conversations?employee_id=${employeeId}${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<ConversationsResponse>;
      if (data.success && data.data) {
        setConversations(data.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  // Load stored presentations (for Slides employee)
  const loadStoredPresentations = async () => {
    if (employeeId !== 'slides') return;
    try {
      const response = await fetch(`${API_URL}/presentations/stored/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: { items: typeof storedPresentations } };
      if (data.success && data.data?.items) {
        setStoredPresentations(data.data.items);
      }
    } catch (error) {
      console.error('Failed to load stored presentations:', error);
    }
  };

  // Load prompt ideas
  const loadPromptIdeas = async () => {
    if (employeeId !== 'slides') return;
    try {
      const response = await fetch(`${API_URL}/presentations/ideas/list?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: { ideas: typeof promptIdeas } };
      if (data.success && data.data?.ideas) {
        setPromptIdeas(data.data.ideas);
      }
    } catch (error) {
      console.error('Failed to load prompt ideas:', error);
    }
  };

  // Track user prompt behavior
  const trackPromptBehavior = async (prompt: string, topic?: string, style?: string, theme?: string, success = true) => {
    if (employeeId !== 'slides') return;
    try {
      await fetch(`${API_URL}/presentations/behavior/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: employeeId,
          prompt,
          extracted_topic: topic,
          extracted_style: style,
          extracted_theme: theme,
          response_success: success,
        }),
      });
    } catch (error) {
      console.error('Failed to track behavior:', error);
    }
  };

  // Load bot config for current employee
  const loadBotConfig = async () => {
    if (!employeeId || !token) return;
    setIsLoadingBotConfig(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/employees/${employeeId}/config${workspaceParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as { success: boolean; data?: { custom_instructions: string | null; tone: string } };
      if (data.success && data.data) {
        setBotConfig({
          custom_instructions: data.data.custom_instructions || '',
          tone: data.data.tone || 'professional',
        });
      }
    } catch (error) {
      console.error('Failed to load bot config:', error);
    } finally {
      setIsLoadingBotConfig(false);
    }
  };

  // Save bot config for current employee
  const saveBotConfig = async () => {
    if (!employeeId || !token) return;
    setIsSavingBotConfig(true);
    try {
      const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const response = await fetch(`${API_URL}/employees/${employeeId}/config${workspaceParam}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          custom_instructions: botConfig.custom_instructions || null,
          tone: botConfig.tone,
        }),
      });
      const data = await response.json() as { success: boolean };
      if (data.success) {
        setShowBotSettingsModal(false);
      }
    } catch (error) {
      console.error('Failed to save bot config:', error);
    } finally {
      setIsSavingBotConfig(false);
    }
  };

  const loadMessages = async (conversationId: number) => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<MessageFromAPI[]>;
      if (data.success && data.data) {
        setMessages(
          data.data.map((m) => ({
            id: m.id.toString(),
            role: m.role as 'user' | 'assistant',
            content: m.content,
            timestamp: new Date(m.created_at),
            tokens_used: m.tokens_used,
          }))
        );
        setShowUseCases(data.data.length === 0);
      } else {
        // Conversation not found or error - clear the invalid conversation
        console.warn('Conversation not found, resetting to new chat');
        setCurrentConversationId(null);
        setSearchParams({});
        setMessages([]);
        setShowUseCases(true);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      // Also handle network errors by resetting
      setCurrentConversationId(null);
      setSearchParams({});
      setMessages([]);
      setShowUseCases(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const createConversation = async (): Promise<number | null> => {
    try {
      const response = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: employeeId,
          title: `Chat with ${employee?.name}`,
          workspace_id: currentWorkspace?.id || null,
        }),
      });
      const data = (await response.json()) as APIResponse<Conversation>;
      if (data.success && data.data) {
        const newConv = data.data;
        setConversations((prev) => [newConv, ...prev]);
        setCurrentConversationId(newConv.id);
        // Mark as new conversation BEFORE setting searchParams
        // This prevents loadMessages from being called and overwriting local state
        setIsNewConversation(true);
        setSearchParams({ conversation: newConv.id.toString() });
        return newConv.id;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  };

  // Helper function to process streaming response
  const processStreamResponse = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    messageContent: string,
    userMessage: Message
  ) => {
    const decoder = new TextDecoder();
    let fullContent = '';
    const generatedImages: Array<{ url: string; style?: string }> = [];
    const generatedPresentations: Array<{ id: string; data: PresentationData; credits: number }> = [];
    let routeSuggestions: RouteSuggestion[] = [];
    const collaborationResults: CollaborationResult[] = [];
    const pendingActions: PendingConnectorAction[] = [];
    const connectorResults: ConnectorResult[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      const lines = text.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            // Streaming complete - clean up any remaining command blocks and markdown images
            let cleanedContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '').trim();
            cleanedContent = cleanedContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?\[\/CREATE_PRESENTATION\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/\[ROUTE_TO:\w+\]/g, '').trim();
            cleanedContent = cleanedContent.replace(/\[INVOKE_EMPLOYEE:\w+\][\s\S]*?(?:\[\/INVOKE_EMPLOYEE\]|$)/g, '').trim();

            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: cleanedContent,
                timestamp: new Date(),
                images: generatedImages.length > 0 ? generatedImages : undefined,
                routeSuggestions: routeSuggestions.length > 0 ? routeSuggestions : undefined,
                collaborations: collaborationResults.length > 0 ? collaborationResults : undefined,
                presentations: generatedPresentations.length > 0 ? generatedPresentations : undefined,
                pendingActions: pendingActions.length > 0 ? pendingActions : undefined,
                connectorResults: connectorResults.length > 0 ? connectorResults : undefined,
              },
            ]);
            setStreamingContent('');
            setCollaboratingWith(null);

            // Auto-show post preview sidebar for Aria when user explicitly asked for content
            // Check if user's message explicitly requests content creation
            const userMessageLower = messageContent.toLowerCase();
            const explicitPostPatterns = [
              /\b(create|write|generate|draft|make|compose)\s+(a\s+)?(social\s+media\s+)?(post|caption|content|ad|copy)/i,
              /\b(post|content|caption)\s+(for|about|on|regarding)\b/i,
              /\b(help|can you|please)\s+(me\s+)?(create|write|generate|draft|make)\s+(a\s+)?(post|content|caption)/i,
              /\b(need|want)\s+(a\s+)?(post|content|caption)/i,
              /\b(schedule|publish)\s+(a\s+)?(post|content)/i,
              /\b(instagram|facebook|twitter|linkedin|tiktok|x)\s+(post|content|caption)/i,
              /\bpost\s+(about|for|on)\b/i,
              /\bsocial\s+media\b/i,
            ];
            const isExplicitPostRequest = explicitPostPatterns.some(pattern => pattern.test(userMessageLower));

            // Check if employee is Aria (case-insensitive)
            const isAriaEmployee = employeeId?.toLowerCase() === 'aria';

            if (isAriaEmployee && cleanedContent.length > 20 && isExplicitPostRequest) {
              // Extract only the actual post content (caption + hashtags)
              // Remove Brain AI reading messages and other system text
              let postContent = cleanedContent;

              // Remove "Reading from Brain AI" and related messages
              postContent = postContent.replace(/📖\s*\*?Reading from Brain AI\.\.\.?\*?\s*/gi, '').trim();
              postContent = postContent.replace(/I'm reviewing your brand and business information to create personalized content:?\s*/gi, '').trim();
              postContent = postContent.replace(/---\s*/g, '').trim();
              postContent = postContent.replace(/✅\s*Brand knowledge loaded successfully\.?\s*/gi, '').trim();
              postContent = postContent.replace(/\*Generating social media graphic\.\.\.?\*/gi, '').trim();

              // Remove "Caption:" prefix if present
              postContent = postContent.replace(/^Caption:\s*/i, '').trim();

              // Remove any "Here's your post:" type prefixes
              postContent = postContent.replace(/^(Here'?s?\s+(your|a|the)\s+(post|caption|content)[:\s]*)/i, '').trim();

              // Check if content has hashtags (common in social media posts)
              const hasHashtags = postContent.includes('#');

              // Show sidebar if: has hashtags OR content is substantial enough to be a post
              // A post is likely if it's between 20-2000 chars and contains social media elements
              const looksLikePost = postContent.length > 20 && postContent.length < 2000;

              if ((hasHashtags || looksLikePost) && postContent.length > 10) {
                // Get image URL if available (from collaboration with Pixel or auto-generated)
                const imageUrl = generatedImages.length > 0 ? generatedImages[0].url : '';

                // Set default publish date to tomorrow at 5 PM
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const publishDate = tomorrow.toISOString().split('T')[0];

                setPostDraft(prev => ({
                  ...prev,
                  content: postContent, // Only the actual caption with hashtags
                  imageUrl: imageUrl,
                  publishDate: publishDate,
                  publishTime: '17:00',
                }));

                // Load integrations for the sidebar (needed for platform selection)
                loadSocialMediaIntegrations();

                // Show the post preview sidebar
                setShowPostPreviewSidebar(true);
                // On mobile, switch to schedule view
                if (window.innerWidth < 768) {
                  setMobileView('schedule');
                }
              }
            }

            // Check if Pixel's response contains [GENERATE_IMAGE] command
            if (employeeId === 'pixel' && fullContent.includes('[GENERATE_IMAGE]')) {
              const promptMatch = fullContent.match(/prompt:\s*(.+?)(?=\n|style:|$)/i);
              if (promptMatch) {
                const imagePrompt = promptMatch[1].trim();
                const logoMatch = messageContent.toLowerCase().match(/logo\s+(?:for\s+)?["']?([^"'\n,]+)["']?/i) ||
                                 messageContent.toLowerCase().match(/(?:create|make|design)\s+(?:a\s+)?logo\s+(?:for\s+)?["']?([^"'\n,]+)["']?/i);

                if (logoMatch) {
                  handleGenerateLogo(logoMatch[1].trim(), { description: imagePrompt });
                } else {
                  handleGenerateImage(imagePrompt, { n: 2 });
                }
              }
            }
          } else {
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                // Strip EDIT_IMAGE blocks, ROUTE_TO commands, INVOKE_EMPLOYEE blocks, CREATE_PRESENTATION blocks, and markdown image URLs from display
                let displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '');
                displayContent = displayContent.replace(/\[ROUTE_TO:\w+\]/g, '');
                displayContent = displayContent.replace(/\[INVOKE_EMPLOYEE:\w+\][\s\S]*?(?:\[\/INVOKE_EMPLOYEE\]|$)/g, '');
                displayContent = displayContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?(?:\[\/CREATE_PRESENTATION\]|$)/g, '');
                displayContent = displayContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '');
                setStreamingContent(displayContent.trim());
              }
              if (parsed.generated_image) {
                generatedImages.push({
                  url: parsed.generated_image,
                  style: parsed.image_type || undefined,
                });
                // Also update postDraft if the sidebar is visible (image arrived after [DONE])
                if (showPostPreviewSidebar) {
                  setPostDraft(prev => ({ ...prev, imageUrl: parsed.generated_image }));
                }
                // Strip EDIT_IMAGE blocks, ROUTE_TO commands, INVOKE_EMPLOYEE blocks, CREATE_PRESENTATION blocks, and markdown image URLs from display
                let displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '');
                displayContent = displayContent.replace(/\[ROUTE_TO:\w+\]/g, '');
                displayContent = displayContent.replace(/\[INVOKE_EMPLOYEE:\w+\][\s\S]*?(?:\[\/INVOKE_EMPLOYEE\]|$)/g, '');
                displayContent = displayContent.replace(/\[CREATE_PRESENTATION\][\s\S]*?(?:\[\/CREATE_PRESENTATION\]|$)/g, '');
                displayContent = displayContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '');
                setStreamingContent(displayContent.trim() + `\n\n*Generating design ${generatedImages.length}...*`);
              }
              // Handle presentation generation
              if (parsed.presentation_generating) {
                setStreamingContent(prev => prev + `\n\n*📊 Creating ${parsed.presentation_slides}-slide presentation about "${parsed.presentation_topic}"...*`);
              }
              if (parsed.generated_presentation) {
                // Store presentation data for display
                generatedPresentations.push({
                  id: parsed.presentation_id,
                  data: parsed.generated_presentation,
                  credits: parsed.credits_used
                });
                setStreamingContent(prev => prev.replace(/\*📊 Creating.*?\.\.\.\*/, '') + `\n\n✅ Presentation created successfully!`);
              }
              // Handle redirect separately - this comes in a separate smaller SSE message
              // to avoid being lost due to JSON parsing issues with large payloads
              if (parsed.redirect_to_builder && parsed.redirect_presentation_id) {
                setTimeout(() => {
                  navigate(`/dashboard/presentations/${parsed.redirect_presentation_id}`);
                }, 1500); // Short delay to show success message
              }
              if (parsed.presentation_error) {
                setStreamingContent(prev => prev + `\n\n❌ ${parsed.presentation_error}`);
              }
              if (parsed.route_suggestions && Array.isArray(parsed.route_suggestions)) {
                routeSuggestions = parsed.route_suggestions;
              }
              // Handle cross-employee collaboration
              if (parsed.collaboration_start) {
                setCollaboratingWith(parsed.collaboration_start.toEmployeeName);
                setStreamingContent(prev => prev + `\n\n*🤝 Collaborating with ${parsed.collaboration_start.toEmployeeName} (${parsed.collaboration_start.toEmployeeRole})...*`);
              }
              if (parsed.collaboration_result) {
                collaborationResults.push({
                  employeeId: parsed.collaboration_result.employeeId,
                  employeeName: parsed.collaboration_result.employeeName,
                  employeeRole: parsed.collaboration_result.employeeRole,
                  content: parsed.collaboration_result.content,
                });
                setCollaboratingWith(null);
              }
              if (parsed.collaboration_error) {
                setCollaboratingWith(null);
              }
              // Handle connector read results (immediate execution)
              if (parsed.connector_result) {
                connectorResults.push({
                  provider: parsed.connector_result.provider,
                  providerName: parsed.connector_result.providerName,
                  action: parsed.connector_result.action,
                  success: parsed.connector_result.success,
                  data: parsed.connector_result.data,
                  error: parsed.connector_result.error,
                });
                // Show status in streaming content
                const resultIcon = parsed.connector_result.success ? '✅' : '❌';
                setStreamingContent(prev => prev + `\n\n*${resultIcon} ${parsed.connector_result.providerName}: ${parsed.connector_result.action} completed*`);
              }
              // Handle connector write actions (need approval)
              if (parsed.pending_action) {
                pendingActions.push({
                  id: parsed.pending_action.id,
                  provider: parsed.pending_action.provider,
                  providerName: parsed.pending_action.providerName,
                  action: parsed.pending_action.action,
                  preview: parsed.pending_action.preview,
                  actionData: parsed.pending_action.actionData,
                  requiresApproval: parsed.pending_action.requiresApproval,
                  status: 'pending',
                });
                setStreamingContent(prev => prev + `\n\n*📝 Action pending approval: ${parsed.pending_action.providerName} - ${parsed.pending_action.action}*`);
              }
              // Handle connector errors
              if (parsed.connector_error) {
                console.error('Connector error:', parsed.connector_error);
                setStreamingContent(prev => prev + `\n\n*❌ ${parsed.connector_error.provider}: ${parsed.connector_error.error}*`);
              }
              if (parsed.done && typeof parsed.credits_remaining === 'number') {
                updateCredits(parsed.credits_remaining);
              }
              if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    }

    // Reload conversations to update last_message_at
    loadConversations();
  };

  const handleSend = async (routedContent?: string) => {
    const messageText = routedContent || input.trim();
    if (!messageText || isLoading || !token) return;

    // Use conversation image for ALL employees if no new upload (allows follow-up questions about images)
    // This enables the AI to remember the image context across multiple messages
    const imageToSend = uploadedImage || conversationImage;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      imageUrl: routedContent ? undefined : (uploadedImage || undefined), // Only show in UI if newly uploaded
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageContent = messageText;
    if (!routedContent) {
      setInput('');
    }
    setUploadedImage(null); // Clear uploaded image preview (conversationImage stays)
    setIsLoading(true);
    setShowUseCases(false);
    setStreamingContent('');

    try {
      // Create conversation if needed or if current one doesn't exist
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming (include image if uploaded or if there's a conversation image for Pixel)
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: messageContent,
          image: imageToSend || undefined, // Send conversation image for follow-up edits
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as APIResponse<unknown>;
        // If conversation not found, create a new one and retry
        if (response.status === 404 && errorData.error === 'Conversation not found') {
          console.warn('Conversation not found, creating new conversation and retrying...');
          setCurrentConversationId(null);
          setSearchParams({});
          convId = await createConversation();
          if (!convId) {
            throw new Error('Failed to create conversation');
          }
          // Retry the message send with new conversation
          const retryResponse = await fetch(`${API_URL}/conversations/${convId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              content: messageContent,
              image: imageToSend || undefined,
            }),
            signal: abortControllerRef.current.signal,
          });
          if (!retryResponse.ok) {
            const retryError = (await retryResponse.json()) as APIResponse<unknown>;
            throw new Error(retryError.error || 'Failed to send message');
          }
          // Continue with the retry response
          const reader = retryResponse.body?.getReader();
          if (!reader) throw new Error('No response body');
          await processStreamResponse(reader, messageContent, userMessage);
          return;
        }
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      await processStreamResponse(reader, messageContent, userMessage);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Send message error:', error);
        // Determine user-friendly error message
        let errorMessage = 'Something went wrong. Please try again.';
        const errorStr = (error as Error).message || '';

        if (errorStr.includes('Failed to fetch') || errorStr.includes('NetworkError') || errorStr.includes('ERR_CONNECTION')) {
          errorMessage = 'Network connection lost. Please check your internet connection and try again.';
        } else if (errorStr.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (errorStr) {
          errorMessage = `Sorry, I encountered an error: ${errorStr}. Please try again.`;
        }

        // Show error message
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: errorMessage,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  };

  // Keep handleSend ref updated so we always have the latest version
  useEffect(() => {
    handleSendRef.current = handleSend;
  });

  // Step 2: Process pending routed message after handleSend is available
  // We also need to ensure employee data is loaded before sending
  useEffect(() => {
    if (pendingRoutedMessage && !isLoading && token && employee && employeeId) {
      const messageToSend = pendingRoutedMessage;
      setPendingRoutedMessage(null); // Clear immediately to prevent double-send

      // Ensure we have a clean state for the new employee
      setCurrentConversationId(null);
      setMessages([]);
      setShowUseCases(false);

      // Delay to ensure React has fully updated all state
      // Use the ref to ensure we call the latest version of handleSend
      const timer = setTimeout(() => {
        if (handleSendRef.current) {
          handleSendRef.current(messageToSend);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pendingRoutedMessage, isLoading, token, employee, employeeId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleUseCaseClick = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const handleNewChat = () => {
    // Clear localStorage image for current conversation before resetting
    saveConversationImageToStorage(currentConversationId, employeeId || '', null);
    setCurrentConversationId(null);
    setMessages([]);
    setShowUseCases(true);
    setSearchParams({});
    // Clear conversation image when starting new chat
    setConversationImage(null);
    setUploadedImage(null);
  };

  const handleSelectConversation = (conv: Conversation) => {
    setCurrentConversationId(conv.id);
    setSearchParams({ conversation: conv.id.toString() });
    loadMessages(conv.id);
    // Load conversation image from localStorage (if any was stored for this conversation)
    const storedImage = loadConversationImageFromStorage(conv.id, employeeId || '');
    setConversationImage(storedImage);
    setUploadedImage(null);
    // Only hide sidebar on mobile when selecting a conversation
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const handleCopy = useCallback(async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const handleRegenerate = useCallback(async () => {
    if (messages.length < 2) return;

    // Get the last user message
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMessage) return;

    // Remove the last assistant message
    setMessages((prev) => {
      const newMessages = [...prev];
      if (newMessages[newMessages.length - 1]?.role === 'assistant') {
        newMessages.pop();
      }
      return newMessages;
    });

    // Re-send the last user message
    setInput(lastUserMessage.content);
    setTimeout(() => handleSend(), 100);
  }, [messages]);

  // Start editing a user message
  const handleStartEdit = useCallback((message: Message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }, []);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent('');
  }, []);

  // Save edited message and re-send
  const handleSaveEdit = useCallback(async (messageId: string) => {
    if (!editingContent.trim() || isLoading || !token) return;

    // Find the message index
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;

    const messageContent = editingContent.trim();

    // Remove all messages after this one (including the edited message)
    const newMessages = messages.slice(0, messageIndex);

    // Add the edited message
    const editedMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
    };

    setMessages([...newMessages, editedMessage]);
    setEditingMessageId(null);
    setEditingContent('');
    setShowUseCases(false);
    setIsLoading(true);
    setStreamingContent('');

    try {
      // Create conversation if needed
      let convId = currentConversationId;
      if (!convId) {
        convId = await createConversation();
        if (!convId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Send message with streaming
      const response = await fetch(`${API_URL}/conversations/${convId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: messageContent,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as APIResponse<unknown>;
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let fullContent = '';
      const generatedImages: Array<{ url: string; style?: string }> = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              // Streaming complete
              let cleanedContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim();
              cleanedContent = cleanedContent.replace(/!\[.*?\]\(https?:\/\/[^\)]+\)/g, '').trim();

              setMessages((prev) => [
                ...prev,
                {
                  id: (Date.now() + 1).toString(),
                  role: 'assistant',
                  content: cleanedContent,
                  timestamp: new Date(),
                  images: generatedImages.length > 0 ? generatedImages : undefined,
                },
              ]);
              setStreamingContent('');
            } else {
              try {
                const parsed = JSON.parse(data);
                if (parsed.content) {
                  fullContent += parsed.content;
                  const displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '').trim();
                  setStreamingContent(displayContent);
                }
                if (parsed.generated_image) {
                  generatedImages.push({
                    url: parsed.generated_image,
                    style: parsed.image_type || undefined,
                  });
                  // Also update postDraft if the sidebar is visible (image arrived after [DONE])
                  if (showPostPreviewSidebar) {
                    setPostDraft(prev => ({ ...prev, imageUrl: parsed.generated_image }));
                  }
                  const displayContent = fullContent.replace(/\[EDIT_IMAGE\][\s\S]*?(?:\[\/EDIT_IMAGE\]|$)/g, '').trim();
                  setStreamingContent(displayContent + `\n\n*Generating design ${generatedImages.length}...*`);
                }
                if (parsed.done && typeof parsed.credits_remaining === 'number') {
                  updateCredits(parsed.credits_remaining);
                }
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }

      // Reload conversations to update last_message_at
      loadConversations();
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Send message error:', error);
        let errorMessage = 'Something went wrong. Please try again.';
        const errorStr = (error as Error).message || '';

        if (errorStr.includes('Failed to fetch') || errorStr.includes('NetworkError') || errorStr.includes('ERR_CONNECTION')) {
          errorMessage = 'Network connection lost. Please check your internet connection and try again.';
        } else if (errorStr.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (errorStr) {
          errorMessage = `Sorry, I encountered an error: ${errorStr}. Please try again.`;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: errorMessage,
            timestamp: new Date(),
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  }, [editingContent, isLoading, messages, token, currentConversationId, updateCredits]);

  // Re-prompt: Use a user message to send again
  const handleReprompt = useCallback((message: Message) => {
    setInput(message.content);
    inputRef.current?.focus();
  }, []);

  // Connector action handlers
  const handleApproveAction = useCallback(async (actionId: number) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      const response = await fetch(`${API_URL}/connectors/pending/${actionId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json() as { success: boolean; data?: unknown; error?: string };

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: result.success ? 'executed' : 'failed', result }
              : action
          ),
        } as Message;
      }));

      if (result.success) {
        // Could show a success toast here
      }
    } catch (error) {
      console.error('Failed to approve action:', error);
      // Update status to failed
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: 'failed', result: { success: false, error: 'Failed to execute action' } }
              : action
          ),
        } as Message;
      }));
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  const handleEditAction = useCallback((actionId: number, action: PendingConnectorAction) => {
    setEditingAction(action);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEditedAction = useCallback(async (actionId: number, editedData: Record<string, unknown>) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      const response = await fetch(`${API_URL}/connectors/pending/${actionId}/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ editedData }),
      });

      const result = await response.json() as { success: boolean; data?: unknown; error?: string };

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: result.success ? 'executed' : 'failed', result, actionData: editedData }
              : action
          ),
        } as Message;
      }));

      setIsEditModalOpen(false);
      setEditingAction(null);
    } catch (error) {
      console.error('Failed to save edited action:', error);
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  const handleRejectAction = useCallback(async (actionId: number) => {
    if (!token) return;
    setActionLoadingId(actionId);

    try {
      await fetch(`${API_URL}/connectors/pending/${actionId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update the action status in the message
      setMessages(prev => prev.map(msg => {
        if (!msg.pendingActions) return msg;
        return {
          ...msg,
          pendingActions: msg.pendingActions.map(action =>
            action.id === actionId
              ? { ...action, status: 'rejected' }
              : action
          ),
        } as Message;
      }));
    } catch (error) {
      console.error('Failed to reject action:', error);
    } finally {
      setActionLoadingId(null);
    }
  }, [token]);

  // Copy user message
  const handleCopyUserMessage = useCallback(async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  // Text-to-speech handler
  const handleSpeak = useCallback((content: string, id: string) => {
    // Check if already speaking this message
    if (speakingMessageId === id) {
      // Stop speaking
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
      return;
    }

    // Stop any current speech
    window.speechSynthesis.cancel();

    // Strip markdown formatting for cleaner speech
    const cleanContent = content
      .replace(/^#{1,6}\s*/gm, '') // Remove heading markers (# ## ### etc.)
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold **text**
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic *text*
      .replace(/__([^_]+)__/g, '$1') // Remove bold __text__
      .replace(/_([^_]+)_/g, '$1') // Remove italic _text_
      .replace(/`([^`]+)`/g, '$1') // Remove inline code `text`
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1 image') // Convert images to "alt text image"
      .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
      .replace(/>\s*/gm, '') // Remove blockquote markers
      .replace(/\n{2,}/g, '. ') // Convert multiple newlines to pauses
      .replace(/\n/g, ' ') // Convert single newlines to spaces
      .trim();

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(cleanContent);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Try to use a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v =>
      v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Samantha'))
    ) || voices.find(v => v.lang.startsWith('en'));

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    // Set callbacks
    utterance.onend = () => {
      setSpeakingMessageId(null);
    };
    utterance.onerror = () => {
      setSpeakingMessageId(null);
    };

    // Start speaking
    setSpeakingMessageId(id);
    window.speechSynthesis.speak(utterance);
  }, [speakingMessageId]);

  // Stop speech when component unmounts or conversation changes
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [currentConversationId]);

  // Handle virtual keyboard on mobile - scroll to keep input visible
  useEffect(() => {
    const handleVisualViewportResize = () => {
      if (window.visualViewport && inputRef.current) {
        // When keyboard opens, the visual viewport height decreases
        // Scroll the chat to keep input visible
        const visualViewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - visualViewportHeight;

        if (keyboardHeight > 100) {
          // Keyboard is open - scroll to bottom to keep input in view
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          }, 100);
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
      };
    }
  }, []);

  // Lock screen orientation to portrait on mobile
  useEffect(() => {
    const lockOrientation = async () => {
      try {
        // Try to lock screen orientation using Screen Orientation API
        if (screen.orientation && 'lock' in screen.orientation) {
          await (screen.orientation as ScreenOrientation & { lock: (orientation: string) => Promise<void> }).lock('portrait');
        }
      } catch (e) {
        // Orientation lock not supported or failed - this is expected on desktop browsers
      }
    };

    lockOrientation();

    return () => {
      // Unlock orientation when leaving the chat
      try {
        if (screen.orientation && 'unlock' in screen.orientation) {
          (screen.orientation as ScreenOrientation & { unlock: () => void }).unlock();
        }
      } catch (e) {
        // Ignore errors
      }
    };
  }, []);

  // Handle file upload for Pixel
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Convert to base64 for preview and sending
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setUploadedImage(base64);
      // Also store as conversation image for follow-up edit requests
      setConversationImage(base64);
      // Save to localStorage for persistence across page reloads
      saveConversationImageToStorage(currentConversationId, employeeId || '', base64);
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Generate logo/design for Pixel (3 credits per logo)
  const handleGenerateLogo = useCallback(async (brandName: string, options?: {
    industry?: string;
    style?: string;
    colors?: string;
    description?: string;
  }) => {
    if (!token || !brandName) return;

    setIsGeneratingImage(true);
    try {
      interface LogoResponse {
        success: boolean;
        data?: {
          logos: GeneratedImage[];
          count: number;
          credits_used?: number;
          credits_remaining?: number;
        };
        error?: string;
      }

      const response = await fetch(`${API_URL}/images/generate-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          brandName,
          industry: options?.industry,
          style: options?.style,
          colors: options?.colors,
          description: options?.description,
          variations: 4,
        }),
      });

      const data = (await response.json()) as LogoResponse;

      if (data.success && data.data) {
        // Update credits if returned
        if (typeof data.data.credits_remaining === 'number') {
          updateCredits(data.data.credits_remaining);
        }

        // Add message with generated images
        const imageMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `I've created ${data.data.count} logo variations for "${brandName}". Click on any image to view it larger, or let me know if you'd like any modifications!`,
          timestamp: new Date(),
          images: data.data.logos,
        };
        setMessages((prev) => [...prev, imageMessage]);
      } else {
        throw new Error(data.error || 'Failed to generate logos');
      }
    } catch (error) {
      console.error('Logo generation error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Sorry, I couldn't generate the logos. ${error instanceof Error ? error.message : 'Please try again.'}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [token, updateCredits]);

  // Generate image from prompt
  // Type determines credit cost: 'image' = 1 credit, 'logo' = 3 credits, 'poster' = 5 credits
  const handleGenerateImage = useCallback(async (prompt: string, options?: {
    style?: string;
    size?: string;
    n?: number;
    type?: 'image' | 'logo' | 'poster';
  }) => {
    if (!token || !prompt) return;

    setIsGeneratingImage(true);
    try {
      interface ImageResponse {
        success: boolean;
        data?: {
          images: GeneratedImage[];
          count: number;
          credits_used?: number;
          credits_remaining?: number;
        };
        error?: string;
      }

      const response = await fetch(`${API_URL}/images/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          style: options?.style || 'vivid',
          size: options?.size || '1024x1024',
          n: options?.n || 1,
          type: options?.type || 'image',
        }),
      });

      const data = (await response.json()) as ImageResponse;

      if (data.success && data.data) {
        // Update credits if returned
        if (typeof data.data.credits_remaining === 'number') {
          updateCredits(data.data.credits_remaining);
        }

        const imageMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Here's the image I created based on your request. Click to view larger or let me know if you'd like any changes!`,
          timestamp: new Date(),
          images: data.data.images,
        };
        setMessages((prev) => [...prev, imageMessage]);
      } else {
        throw new Error(data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      // Show error message to user
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Sorry, I couldn't generate the image. ${error instanceof Error ? error.message : 'Please try again later.'}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [token, updateCredits]);

  // Download image - uses proxy to bypass CORS for DALL-E images
  const handleDownloadImage = useCallback(async (url: string, filename: string) => {
    try {
      // Check if URL is from DALL-E (Azure blob storage) - needs proxy
      const needsProxy = url.includes('blob.core.windows.net') || url.includes('openai.com');

      let downloadUrl = url;
      if (needsProxy) {
        // Use our proxy endpoint to bypass CORS
        downloadUrl = `${API_URL}/images/proxy?url=${encodeURIComponent(url)}`;
      }

      const response = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to download image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open image in new tab
      window.open(url, '_blank');
    }
  }, [token]);

  // Clear current conversation
  const handleClearChat = useCallback(() => {
    if (confirm('Are you sure you want to clear this conversation? This cannot be undone.')) {
      setMessages([]);
      setShowUseCases(true);
      setShowMoreMenu(false);
    }
  }, []);

  // Export chat as text file
  const handleExportChat = useCallback(() => {
    if (messages.length === 0) {
      alert('No messages to export');
      return;
    }

    const exportContent = messages.map(m => {
      const sender = m.role === 'user' ? 'You' : employee?.name || 'Assistant';
      const dateStr = m.timestamp.toLocaleDateString();
      const timeStr = formatTime(m.timestamp);
      return `[${dateStr} ${timeStr}] ${sender}:\n${m.content}\n`;
    }).join('\n---\n\n');

    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-${employee?.name || 'export'}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowMoreMenu(false);
  }, [messages, employee]);

  // Check if message content contains logo/design request for Pixel
  const isDesignRequest = useCallback((content: string): boolean => {
    if (employeeId !== 'pixel') return false;
    const designKeywords = ['logo', 'design', 'create', 'make', 'generate', 'brand', 'icon', 'graphic'];
    const lowerContent = content.toLowerCase();
    return designKeywords.some(keyword => lowerContent.includes(keyword));
  }, [employeeId]);

  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${isDark ? 'bg-dark-800' : 'bg-slate-200'}`}>
          <Bot className={`w-10 h-10 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
        </div>
        <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Employee not found
        </h2>
        <p className={`mb-6 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>This AI employee doesn't exist or has been removed.</p>
        <Link to="/dashboard/employees" className="px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl text-white font-medium transition-colors">
          Back to Employees
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen lg:ml-[68px] relative z-10 overflow-hidden">

      {/* Mobile Full-Screen Chat List View */}
      {isMobile && mobileView === 'list' && (
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`fixed inset-0 z-50 flex flex-col ${isDark ? 'bg-dark-900' : 'bg-white'}`}
        >
          {/* Mobile Chat List Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                <span className="text-sm">{employee.avatar}</span>
              </div>
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Chats</span>
            </div>
            <button
              onClick={() => setMobileView('chat')}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* New Chat Button */}
          <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
            <motion.button
              onClick={() => {
                handleNewChat();
                setMobileView('chat');
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-white transition-all ${isDark ? 'bg-purple-500 hover:bg-purple-600' : 'bg-slate-900 hover:bg-slate-800'}`}
            >
              <Plus className="w-5 h-5" />
              <span>New Chat</span>
            </motion.button>
          </div>

          {/* Employee Expertise */}
          <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
            <p className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              {employee?.name}'s Expertise
            </p>
            <div className="flex flex-wrap gap-2">
              {employee?.capabilities.slice(0, 4).map((cap, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    handleUseCaseClick(`Help me with ${cap.toLowerCase()}`);
                    setMobileView('chat');
                  }}
                  className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                    isDark
                      ? 'bg-white/5 hover:bg-white/10 text-gray-300'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}
                >
                  {cap}
                </button>
              ))}
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <p className={`text-xs uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              Recent Chats
            </p>
            {conversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>No conversations yet</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>Start a new chat to begin</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <motion.button
                    key={conv.id}
                    onClick={() => {
                      handleSelectConversation(conv);
                      setMobileView('chat');
                    }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left p-4 rounded-xl transition-all ${
                      currentConversationId === conv.id
                        ? isDark ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'
                        : isDark ? 'bg-dark-800 hover:bg-dark-700' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        currentConversationId === conv.id
                          ? 'bg-purple-500/20'
                          : isDark ? 'bg-dark-700' : 'bg-slate-200'
                      }`}>
                        <MessageSquare className={`w-5 h-5 ${
                          currentConversationId === conv.id
                            ? 'text-purple-400'
                            : isDark ? 'text-gray-400' : 'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {conv.title}
                        </p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                          {conv.message_count} {conv.message_count === 1 ? 'message' : 'messages'}
                        </p>
                      </div>
                      {currentConversationId === conv.id && (
                        <Check className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ChatGPT-style Sidebar - Desktop Only */}
      <AnimatePresence mode="wait">
        {showSidebar && !isMobile && (
          <>
            {/* Sidebar */}
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`relative flex flex-col transition-all duration-300 ${
                sidebarCollapsed ? 'w-16' : 'w-72'
              } ${isDark ? 'bg-dark-900 border-r border-white/5' : 'bg-slate-50 border-r border-slate-200'}`}
            >
              {/* Sidebar Header Spacer - matches main header height, empty space above border */}
              <div className={`px-3 py-2 md:py-3 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                {/* Empty spacer to align with main header */}
              </div>

              {/* New Chat Button + Collapse Icon - Below the header line, on same row */}
              <div className={`px-3 py-3 border-b ${isDark ? 'border-white/5' : 'border-slate-200'} ${sidebarCollapsed ? 'px-2 flex flex-col items-center gap-2' : 'flex items-center gap-2'}`}>
                {sidebarCollapsed ? (
                  <>
                    <button
                      onClick={handleNewChat}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                      title="New Chat"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'}`}
                      title="Expand sidebar"
                    >
                      <PanelLeft className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <motion.button
                      onClick={handleNewChat}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-white transition-all ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-slate-900 hover:bg-slate-800'}`}
                      title="New Chat"
                    >
                      <Plus className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">New chat</span>
                    </motion.button>
                    <button
                      onClick={() => setSidebarCollapsed(true)}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-200 text-slate-500'}`}
                      title="Collapse sidebar"
                    >
                      <PanelLeftClose className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>

              {/* Employee Expertise Tasks - Only show when not collapsed */}
              {!sidebarCollapsed && (
                <div className={`px-3 py-3 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                  <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    {employee?.name}'s Expertise
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {employee?.capabilities.slice(0, 3).map((cap, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleUseCaseClick(`Help me with ${cap.toLowerCase()}`)}
                        className={`text-[11px] px-2 py-1 rounded-md transition-colors truncate max-w-full ${
                          isDark
                            ? 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white'
                            : 'bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {cap}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stored Presentations Section (only for Slides employee) */}
              {employeeId === 'slides' && !sidebarCollapsed && storedPresentations.length > 0 && (
                <div className={`px-3 py-2 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                  <button
                    onClick={() => setShowStoredSection(!showStoredSection)}
                    className={`w-full flex items-center justify-between text-left ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider flex items-center gap-2">
                      <Download className="w-3 h-3" />
                      Store ({storedPresentations.length})
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showStoredSection ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {showStoredSection && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-2 space-y-1">
                          {storedPresentations.slice(0, 5).map((pres) => (
                            <Link
                              key={pres.id}
                              to={`/dashboard/presentations/${pres.id}`}
                              className={`block px-2 py-1.5 rounded-lg text-sm truncate transition-colors ${
                                isDark
                                  ? 'hover:bg-white/5 text-gray-400 hover:text-white'
                                  : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-3 h-3 flex-shrink-0 text-purple-500" />
                                <span className="truncate">{pres.title}</span>
                              </div>
                              <span className={`text-[10px] ml-5 ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>
                                {pres.data?.slides?.length || 0} slides
                              </span>
                            </Link>
                          ))}
                          {storedPresentations.length > 5 && (
                            <p className={`text-[10px] px-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                              +{storedPresentations.length - 5} more stored
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Prompt Ideas Section (only for Slides employee) */}
              {employeeId === 'slides' && !sidebarCollapsed && promptIdeas.length > 0 && (
                <div className={`px-3 py-2 border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                  <button
                    onClick={() => setShowIdeasSection(!showIdeasSection)}
                    className={`w-full flex items-center justify-between text-left ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider flex items-center gap-2">
                      <Zap className="w-3 h-3 text-yellow-500" />
                      Ideas
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showIdeasSection ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {showIdeasSection && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-2 space-y-1">
                          {promptIdeas.slice(0, 5).map((idea) => (
                            <button
                              key={idea.id}
                              onClick={() => {
                                setInput(idea.suggested_prompt);
                                inputRef.current?.focus();
                              }}
                              className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors ${
                                isDark
                                  ? 'hover:bg-white/5 text-gray-400 hover:text-white'
                                  : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                  isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                                }`}>
                                  {idea.idea_category}
                                </span>
                                <span className="truncate">{idea.idea_title}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Conversations List */}
              <div className={`flex-1 overflow-y-auto py-2 space-y-0.5 ${sidebarCollapsed ? 'px-1' : 'px-2'}`}>
                {!sidebarCollapsed && (
                  <p className={`text-[10px] uppercase tracking-wider px-2 py-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                    Recent Chats
                  </p>
                )}
                {conversations.length === 0 ? (
                  !sidebarCollapsed && (
                    <div className="text-center py-6">
                      <MessageSquare className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>No conversations yet</p>
                    </div>
                  )
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`w-full text-left rounded-lg transition-all group ${
                        sidebarCollapsed ? 'p-2 justify-center' : 'px-3 py-2'
                      } ${
                        currentConversationId === conv.id
                          ? isDark ? 'bg-white/10 text-white' : 'bg-slate-200 text-slate-900'
                          : isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-slate-100 text-slate-600'
                      }`}
                      title={sidebarCollapsed ? conv.title : undefined}
                    >
                      {sidebarCollapsed ? (
                        <div className="flex flex-col items-center">
                          <MessageSquare className="w-4 h-4" />
                          {conv.message_count > 0 && (
                            <span className={`text-[9px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                              {conv.message_count}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <span className="truncate text-sm block">{conv.title}</span>
                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                              {conv.message_count} {conv.message_count === 1 ? 'message' : 'messages'}
                            </span>
                          </div>
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>

            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col min-w-0 min-h-0 w-full ${isDark ? 'bg-dark-900' : 'bg-white'}`}>
        {/* ChatGPT-style Header with employee dropdown */}
        <div className={`flex items-center justify-between px-3 py-2 md:px-4 md:py-3 border-b relative z-30 ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile Chat List Toggle - Only visible on mobile */}
            <button
              onClick={() => setMobileView('list')}
              className={`md:hidden p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'}`}
              title="Open chat history"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Employee Dropdown Selector */}
            <div className="relative" ref={employeeDropdownRef}>
              <button
                onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${employee.gradient} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-sm">{employee.avatar}</span>
                </div>
                <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{employee.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showEmployeeDropdown ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
              </button>

              {/* Employee Dropdown */}
              <AnimatePresence>
                {showEmployeeDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute left-0 top-full mt-2 w-64 max-h-80 overflow-y-auto border rounded-xl shadow-xl z-50 ${
                      isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="py-2">
                      <p className={`px-3 py-1.5 text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        Switch Employee
                      </p>
                      {AI_EMPLOYEES.filter(e => !e.isComingSoon).map((emp) => (
                        <button
                          key={emp.id}
                          onClick={() => {
                            navigate(`/dashboard/employees/${emp.id}`);
                            setShowEmployeeDropdown(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors ${
                            emp.id === employeeId
                              ? isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-900'
                              : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${emp.gradient} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-base">{emp.avatar}</span>
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <div className="text-sm font-medium truncate">{emp.name}</div>
                            <div className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{emp.role}</div>
                          </div>
                          {emp.id === employeeId && (
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {/* More Menu */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {/* More Menu Dropdown */}
              <AnimatePresence>
                {showMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute right-0 top-full mt-2 w-48 border rounded-xl shadow-xl overflow-hidden z-50 ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          loadBotConfig();
                          setShowBotSettingsModal(true);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Settings className="w-4 h-4" />
                        Bot Settings
                      </button>
                      <button
                        onClick={handleExportChat}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Download className="w-4 h-4" />
                        Export Chat
                      </button>
                      <button
                        onClick={handleClearChat}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Clear Chat
                      </button>
                      <div className={`border-t my-1 ${isDark ? 'border-white/5' : 'border-slate-200'}`} />
                      <Link
                        to="/dashboard/employees"
                        onClick={() => setShowMoreMenu(false)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Users className="w-4 h-4" />
                        All Employees
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden px-3 md:px-6 py-3 md:py-4 relative z-10"
        >
          {/* Routed from another employee indicator */}
          {routedFrom && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto mb-4"
            >
              <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm ${
                isDark
                  ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 text-purple-300'
                  : 'bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 text-purple-700'
              }`}>
                <Users className="w-4 h-4 flex-shrink-0" />
                <span>
                  Routed from <span className="font-semibold">{routedFrom}</span> — {employee?.name} will continue helping you with your request.
                </span>
                <button
                  onClick={() => setRoutedFrom(null)}
                  className={`ml-auto p-1 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10' : 'hover:bg-purple-100'
                  }`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Loading History */}
          {isLoadingHistory && (
            <div className="flex justify-center py-8">
              <div className={`flex items-center gap-3 px-4 py-2 rounded-full ${isDark ? 'bg-dark-800/50' : 'bg-white shadow-sm'}`}>
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Loading conversation...</span>
              </div>
            </div>
          )}

          {/* Welcome Message - ChatGPT style centered */}
          {messages.length === 0 && !isLoadingHistory && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center py-8 md:py-16 px-4"
            >
              <motion.div
                className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mx-auto mb-4 shadow-xl`}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <span className="text-2xl md:text-3xl">{employee.avatar}</span>
              </motion.div>
              <h2 className={`text-xl md:text-2xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                How can I help you today?
              </h2>
              <p className={`text-sm max-w-md mx-auto mb-6 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                I'm {employee.name}, {employee.role.toLowerCase()}. {employee.description}
              </p>

              {/* Quick Actions - ChatGPT style grid */}
              {showUseCases && (
                <div className="max-w-lg mx-auto">
                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    {employee.capabilities.slice(0, 4).map((capability, index) => (
                      <motion.button
                        key={capability}
                        onClick={() => handleUseCaseClick(`Help me with ${capability.toLowerCase()}`)}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-3 md:p-4 rounded-xl border text-left transition-all ${
                          isDark
                            ? 'bg-dark-800/50 border-white/10 hover:border-purple-500/30 hover:bg-dark-800'
                            : 'bg-slate-50 border-slate-200 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                      >
                        <span className={`text-xs md:text-sm line-clamp-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                          {capability}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                  {/* See More Prompts Button */}
                  {employee.capabilities.length > 4 && (
                    <motion.button
                      onClick={() => setShowMorePromptsModal(true)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.25 }}
                      className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 mx-auto ${
                        isDark
                          ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
                          : 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      See More Prompts
                    </motion.button>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Messages - Centered like ChatGPT */}
          <div className="max-w-3xl mx-auto space-y-3 md:space-y-4 w-full">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex w-full gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {/* Avatar - ChatGPT style */}
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                        <span className="text-sm">{employee.avatar}</span>
                      </div>
                    </div>
                  )}

                  <div className={`${message.role === 'user' ? 'flex justify-end' : ''}`}>
                    {/* Message Content - ChatGPT style */}
                    <div
                      className={`rounded-2xl px-4 py-3 min-w-0 overflow-hidden ${
                        message.role === 'user'
                          ? `bg-purple-500 text-white max-w-fit`
                          : isDark
                            ? 'bg-dark-800'
                            : 'bg-slate-100'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className={`flex items-center gap-2 mb-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          <span className="font-medium">{employee.name}</span>
                          <span>•</span>
                          <span>{formatTime(message.timestamp)}</span>
                        </div>
                      )}

                      {message.role === 'user' ? (
                        <>
                          {editingMessageId === message.id ? (
                            // Editing UI
                            <div className="space-y-2">
                              <textarea
                                value={editingContent}
                                onChange={(e) => setEditingContent(e.target.value)}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:border-purple-400 resize-none"
                                rows={3}
                                autoFocus
                              />
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveEdit(message.id)}
                                  disabled={!editingContent.trim() || isLoading}
                                  className="px-3 py-1.5 text-xs rounded-lg bg-purple-500 hover:bg-purple-600 text-white transition-colors disabled:opacity-50"
                                >
                                  Save & Send
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal message display
                            <>
                              <div className="text-sm leading-relaxed whitespace-pre-wrap text-white">
                                {message.content}
                              </div>
                              {/* Show uploaded image in user message */}
                              {message.imageUrl && (
                                <div className="mt-3">
                                  <img
                                    src={message.imageUrl}
                                    alt="Uploaded"
                                    className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => setShowImagePreview(message.imageUrl || null)}
                                  />
                                </div>
                              )}
                              {/* User message actions */}
                              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/20">
                                <motion.button
                                  onClick={() => handleCopyUserMessage(message.content, message.id)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                  title="Copy"
                                >
                                  {copiedId === message.id ? (
                                    <Check className="w-3.5 h-3.5 text-green-300" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5 text-white/70 hover:text-white" />
                                  )}
                                </motion.button>
                                <motion.button
                                  onClick={() => handleStartEdit(message)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                  title="Edit"
                                  disabled={isLoading}
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-white/70 hover:text-white" />
                                </motion.button>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <MarkdownRenderer content={message.content} className="text-sm break-words overflow-hidden" isDark={isDark} />
                          {/* Show generated images */}
                          {message.images && message.images.length > 0 && (
                            <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
                              {message.images.map((img, imgIndex) => (
                                <div key={imgIndex} className={`relative group ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'} rounded-xl sm:rounded-2xl p-2 sm:p-3`}>
                                  {/* Image label */}
                                  <div className={`flex items-center justify-between mb-2 px-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                    <span className="text-xs sm:text-sm font-medium">
                                      {img.style ? `${img.style.charAt(0).toUpperCase() + img.style.slice(1)} Design` : `Design ${imgIndex + 1}`}
                                    </span>
                                    <span className="text-xs">Variation {imgIndex + 1} of {message.images?.length}</span>
                                  </div>
                                  {/* Image */}
                                  <img
                                    src={img.url}
                                    alt={img.style || `Generated design ${imgIndex + 1}`}
                                    className={`w-full rounded-lg sm:rounded-xl cursor-pointer hover:opacity-90 transition-all border ${isDark ? 'border-white/10 hover:border-purple-500/50' : 'border-slate-200 hover:border-purple-400'}`}
                                    onClick={() => setShowImagePreview(img.url)}
                                  />
                                  {/* Download button - always visible on mobile, hover on desktop */}
                                  <div className="flex items-center justify-between mt-2 sm:mt-3">
                                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                      Click to enlarge
                                    </span>
                                    <motion.button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadImage(img.url, `design-variation-${imgIndex + 1}.png`);
                                      }}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                                        isDark
                                          ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                                          : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                      }`}
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                      <span>Download</span>
                                    </motion.button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Route Suggestions - Connect with Specialist buttons */}
                          {message.routeSuggestions && message.routeSuggestions.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Users className="w-3.5 h-3.5" />
                                <span>Recommended Specialists:</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {message.routeSuggestions.map((suggestion, idx) => {
                                  const targetEmployee = getEmployeeById(suggestion.employeeId);
                                  // Find the user's message that triggered this routing suggestion
                                  const userMessage = messages.slice(0, index).reverse().find(m => m.role === 'user');
                                  const routedMessage = userMessage?.content || '';
                                  return (
                                    <motion.button
                                      key={idx}
                                      onClick={() => {
                                        // Navigate with the original user message as a routed parameter
                                        // Use encodeURIComponent for proper URL encoding
                                        const params = new URLSearchParams();
                                        params.set('routed', 'true');
                                        params.set('from', employeeId || 'agent');
                                        params.set('message', encodeURIComponent(routedMessage));
                                        navigate(`/dashboard/employees/${suggestion.employeeId}?${params.toString()}`);
                                      }}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                                        isDark
                                          ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30 hover:border-purple-500/50 text-white'
                                          : 'bg-gradient-to-r from-purple-50 to-cyan-50 border-purple-200 hover:border-purple-400 text-slate-900'
                                      }`}
                                    >
                                      {targetEmployee && (
                                        <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${targetEmployee.gradient} flex items-center justify-center`}>
                                          <span className="text-xs">{targetEmployee.avatar}</span>
                                        </div>
                                      )}
                                      <div className="text-left">
                                        <div className="text-sm font-medium">Connect with {suggestion.employeeName}</div>
                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                          {suggestion.employeeRole}
                                        </div>
                                      </div>
                                      <ArrowRight className={`w-4 h-4 ml-1 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
                                    </motion.button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Cross-Employee Collaboration Results */}
                          {message.collaborations && message.collaborations.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Users className="w-3.5 h-3.5" />
                                <span>Collaboration Results:</span>
                              </div>
                              {message.collaborations.map((collab, idx) => {
                                const collabEmployee = getEmployeeById(collab.employeeId);
                                return (
                                  <div
                                    key={idx}
                                    className={`rounded-xl border p-4 ${
                                      isDark
                                        ? 'bg-gradient-to-r from-cyan-500/5 to-purple-500/5 border-cyan-500/20'
                                        : 'bg-gradient-to-r from-cyan-50 to-purple-50 border-cyan-200'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-3">
                                      {collabEmployee && (
                                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${collabEmployee.gradient} flex items-center justify-center`}>
                                          <span className="text-sm">{collabEmployee.avatar}</span>
                                        </div>
                                      )}
                                      <div>
                                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                          {collab.employeeName}
                                        </div>
                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                          {collab.employeeRole}
                                        </div>
                                      </div>
                                    </div>
                                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                      <MarkdownRenderer content={collab.content.replace(/\[EDIT_IMAGE\][\s\S]*?\[\/EDIT_IMAGE\]/g, '').trim()} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Generated Presentations */}
                          {message.presentations && message.presentations.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {message.presentations.map((presentation, presIdx) => (
                                <div
                                  key={presIdx}
                                  className={`rounded-2xl border overflow-hidden ${
                                    isDark
                                      ? 'bg-gradient-to-br from-orange-500/10 via-red-500/5 to-purple-500/10 border-orange-500/30'
                                      : 'bg-gradient-to-br from-orange-50 via-red-50 to-purple-50 border-orange-200'
                                  }`}
                                >
                                  {/* Presentation Header */}
                                  <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg`}>
                                          <span className="text-lg">📊</span>
                                        </div>
                                        <div>
                                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            {presentation.data.title}
                                          </h3>
                                          {presentation.data.subtitle && (
                                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                              {presentation.data.subtitle}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                                        isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-600'
                                      }`}>
                                        <Zap className="w-3 h-3" />
                                        <span>{presentation.credits} credits</span>
                                      </div>
                                    </div>
                                    <div className={`flex items-center gap-4 mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                      <span className="flex items-center gap-1">
                                        <span className="font-medium">{presentation.data.slides.length}</span> slides
                                      </span>
                                      <span className="capitalize">{presentation.data.style} style</span>
                                      <span className="capitalize">{presentation.data.theme} theme</span>
                                    </div>
                                  </div>

                                  {/* Slides Preview */}
                                  <div className="p-4 max-h-[500px] overflow-y-auto">
                                    <div className="space-y-3">
                                      {presentation.data.slides.map((slide, slideIdx) => (
                                        <motion.div
                                          key={slideIdx}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: slideIdx * 0.05 }}
                                          className={`rounded-xl border p-4 ${
                                            isDark
                                              ? 'bg-dark-800/50 border-white/10 hover:border-orange-500/30'
                                              : 'bg-white border-slate-200 hover:border-orange-300'
                                          } transition-colors`}
                                        >
                                          <div className="flex items-start gap-3">
                                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                              isDark
                                                ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 text-orange-400'
                                                : 'bg-gradient-to-br from-orange-100 to-red-100 text-orange-600'
                                            }`}>
                                              {slide.number}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h4 className={`font-semibold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                {slide.title}
                                              </h4>
                                              <ul className={`space-y-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                                {slide.content.map((point, pointIdx) => (
                                                  <li key={pointIdx} className="flex items-start gap-2">
                                                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                                      isDark ? 'bg-orange-400' : 'bg-orange-500'
                                                    }`} />
                                                    <span>{point}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                              {slide.notes && (
                                                <div className={`mt-3 pt-2 border-t text-xs italic ${
                                                  isDark ? 'border-white/5 text-gray-500' : 'border-slate-100 text-slate-400'
                                                }`}>
                                                  💡 {slide.notes}
                                                </div>
                                              )}
                                              {(slide.animation || slide.transition) && (
                                                <div className={`flex items-center gap-3 mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                                  {slide.animation && (
                                                    <span className="flex items-center gap-1">
                                                      ✨ {slide.animation}
                                                    </span>
                                                  )}
                                                  {slide.transition && (
                                                    <span className="flex items-center gap-1">
                                                      🔄 {slide.transition}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Export Actions */}
                                  <div className={`p-4 border-t ${isDark ? 'border-white/10 bg-dark-800/30' : 'border-slate-200 bg-slate-50'}`}>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <motion.button
                                        onClick={() => {
                                          const json = JSON.stringify(presentation.data, null, 2);
                                          const blob = new Blob([json], { type: 'application/json' });
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `${presentation.data.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isDark
                                            ? 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30'
                                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                                        }`}
                                      >
                                        <Download className="w-4 h-4" />
                                        <span>Export JSON</span>
                                      </motion.button>
                                      <motion.button
                                        onClick={() => {
                                          navigator.clipboard.writeText(JSON.stringify(presentation.data, null, 2));
                                          setCopiedId(`pres-${presentation.id}`);
                                          setTimeout(() => setCopiedId(null), 2000);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                          isDark
                                            ? 'bg-white/5 text-gray-300 hover:bg-white/10'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                      >
                                        {copiedId === `pres-${presentation.id}` ? (
                                          <>
                                            <Check className="w-4 h-4 text-green-400" />
                                            <span>Copied!</span>
                                          </>
                                        ) : (
                                          <>
                                            <Copy className="w-4 h-4" />
                                            <span>Copy JSON</span>
                                          </>
                                        )}
                                      </motion.button>
                                    </div>
                                    <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                      Export to use in Gamma, Canva, PowerPoint, or Google Slides
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Connector Results (READ actions) */}
                          {message.connectorResults && message.connectorResults.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Zap className="w-3.5 h-3.5" />
                                <span>Integration Data:</span>
                              </div>
                              {message.connectorResults.map((result, idx) => (
                                <ConnectorResultCard
                                  key={idx}
                                  provider={result.provider}
                                  providerName={result.providerName}
                                  action={result.action}
                                  success={result.success}
                                  data={result.data}
                                  error={result.error}
                                />
                              ))}
                            </div>
                          )}

                          {/* Connector Pending Actions (WRITE actions needing approval) */}
                          {message.pendingActions && message.pendingActions.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                <Zap className="w-3.5 h-3.5" />
                                <span>Actions:</span>
                              </div>
                              {message.pendingActions.map((action, idx) => (
                                <ConnectorActionCard
                                  key={idx}
                                  action={action}
                                  onApprove={handleApproveAction}
                                  onEdit={handleEditAction}
                                  onReject={handleRejectAction}
                                  isLoading={actionLoadingId === action.id}
                                  status={action.status}
                                  result={action.result}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}

                      {/* Message Actions (for assistant messages) - ChatGPT style */}
                      {message.role === 'assistant' && (
                        <div className={`flex items-center gap-1 mt-3 pt-2 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                          <motion.button
                            onClick={() => handleCopy(message.content, message.id)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-500 hover:text-gray-300' : 'hover:bg-slate-200 text-slate-400 hover:text-slate-600'}`}
                            title="Copy"
                          >
                            {copiedId === message.id ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </motion.button>
                          <motion.button
                            onClick={() => handleSpeak(message.content, message.id)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-500 hover:text-gray-300' : 'hover:bg-slate-200 text-slate-400 hover:text-slate-600'}`}
                            title={speakingMessageId === message.id ? "Stop" : "Listen"}
                          >
                            {speakingMessageId === message.id ? (
                              <VolumeX className="w-4 h-4 text-purple-400" />
                            ) : (
                              <Volume2 className="w-4 h-4" />
                            )}
                          </motion.button>
                          <motion.button
                            onClick={handleRegenerate}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors hidden md:block ${isDark ? 'hover:bg-white/10 text-gray-500 hover:text-gray-300' : 'hover:bg-slate-200 text-slate-400 hover:text-slate-600'}`}
                            title="Regenerate"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-500 hover:text-green-400' : 'hover:bg-slate-200 text-slate-400 hover:text-green-500'}`}
                            title="Like"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-500 hover:text-red-400' : 'hover:bg-slate-200 text-slate-400 hover:text-red-500'}`}
                            title="Dislike"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </motion.button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Streaming Response - ChatGPT style */}
            {streamingContent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3"
                layout={false}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                    <span className="text-sm">{employee.avatar}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`rounded-2xl px-4 py-3 ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}
                    style={{ contain: 'layout style' }}
                  >
                    <div className={`flex items-center gap-2 mb-2 text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      <span className="font-medium">{employee.name}</span>
                      <span className="text-purple-400">typing...</span>
                    </div>
                    <div className="text-sm leading-relaxed" style={{ contain: 'layout' }}>
                      <MarkdownRenderer content={streamingContent} isDark={isDark} />
                      <span className="inline-block w-2 h-4 bg-purple-400 ml-0.5 animate-pulse" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Loading Indicator - ChatGPT style */}
            {isLoading && !streamingContent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                    <span className="text-sm">{employee.avatar}</span>
                  </div>
                </div>
                <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-1.5 h-1.5 bg-purple-400 rounded-full"
                          animate={{ y: [0, -4, 0] }}
                          transition={{
                            duration: 0.5,
                            repeat: Infinity,
                            delay: i * 0.12,
                          }}
                        />
                      ))}
                    </div>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Thinking...</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - ChatGPT style - minimal padding, centered */}
        <div className={`flex-shrink-0 px-3 py-1.5 md:px-6 md:py-2 ${isDark ? 'bg-dark-900' : 'bg-white'}`}>
          <div className="max-w-3xl mx-auto">
            {/* Uploaded image preview */}
            {uploadedImage && (
              <div className="mb-2 flex items-start gap-2">
                <div className="relative">
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className={`h-14 rounded-lg border ${isDark ? 'border-white/10' : 'border-slate-200'}`}
                  />
                  <button
                    onClick={() => setUploadedImage(null)}
                    className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Image generating indicator */}
            {isGeneratingImage && (
              <div className="mb-2 flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin flex-shrink-0" />
                <span className="text-xs text-purple-300">Generating designs...</span>
              </div>
            )}

            {/* ChatGPT-style input container */}
            <div className={`relative flex items-end rounded-xl border transition-colors shadow-sm ${isDark ? 'bg-dark-800 border-white/10 focus-within:border-purple-500/50' : 'bg-slate-50 border-slate-200 focus-within:border-purple-400'}`}>
              {/* Attachment Button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`flex-shrink-0 p-2.5 rounded-lg transition-colors ${
                  employeeId === 'pixel'
                    ? 'hover:bg-pink-500/10 text-pink-400'
                    : isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-slate-100 text-slate-400'
                }`}
                title={employeeId === 'pixel' ? 'Upload image to edit' : 'Attach file'}
              >
                <Paperclip className="w-5 h-5" />
              </button>

              {/* Text Input - 16px font prevents iOS zoom on focus */}
              {/* Text box is always enabled so users can type their next message while waiting */}
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={employeeId === 'pixel'
                  ? 'Describe your design...'
                  : isLoading
                    ? 'Type your next message...'
                    : `Message ${employee.name}...`
                }
                rows={1}
                className={`flex-1 py-2.5 pr-2 bg-transparent text-base resize-none focus:outline-none max-h-[100px] min-h-[24px] ${isDark ? 'text-white placeholder-gray-500' : 'text-slate-900 placeholder-slate-400'}`}
                style={{ lineHeight: '1.5', fontSize: '16px' }}
              />

              {/* Voice Input Button */}
              {voiceSupported && (
                <button
                  onClick={handleVoiceInput}
                  className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                    isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : isDark
                        ? 'hover:bg-white/5 text-gray-400 hover:text-white'
                        : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                  }`}
                  title={isListening ? 'Stop listening' : 'Voice input'}
                >
                  <Mic className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
                </button>
              )}

              {/* Send Button */}
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading || isGeneratingImage}
                className={`flex-shrink-0 p-2.5 m-1 rounded-lg transition-all ${
                  input.trim() && !isLoading && !isGeneratingImage
                    ? 'bg-purple-500 hover:bg-purple-600 text-white'
                    : isDark ? 'bg-dark-600 text-gray-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLoading || isGeneratingImage ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Credits Info - Minimal, single line */}
            <div className="flex items-center justify-center mt-1.5 gap-1">
              <Sparkles className="w-2.5 h-2.5 text-purple-400" />
              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                {user?.credits_remaining?.toLocaleString() || 0} credits
              </span>
              {employeeId === 'pixel' && (
                <span className={`text-[10px] hidden md:inline ml-1 ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>
                  • Ask me to create logos!
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {showImagePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImagePreview(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={showImagePreview}
                alt="Preview"
                className="max-w-full max-h-[90vh] rounded-xl object-contain"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                <motion.button
                  onClick={() => handleDownloadImage(showImagePreview, 'design.png')}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 bg-purple-500 rounded-xl text-white hover:bg-purple-600"
                  title="Download"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </motion.button>
                <motion.button
                  onClick={() => setShowImagePreview(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 bg-white/10 rounded-xl text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Social Media Post Preview Sidebar */}
      <AnimatePresence>
        {showPostPreviewSidebar && (
          <>
            {/* Backdrop - hidden on mobile when in schedule view */}
            {(!isMobile || mobileView !== 'schedule') && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 z-40"
                onClick={() => {
                  setShowPostPreviewSidebar(false);
                  if (isMobile) setMobileView('chat');
                }}
              />
            )}

            {/* Sidebar - Full screen on mobile */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`fixed top-0 right-0 h-full z-50 shadow-2xl overflow-y-auto ${
                isMobile ? 'w-full' : 'w-full max-w-md'
              } ${isDark ? 'bg-dark-800' : 'bg-white'}`}
            >
              {/* Sidebar Header */}
              <div className={`sticky top-0 z-10 p-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      setShowPostPreviewSidebar(false);
                      if (isMobile) setMobileView('chat');
                    }}
                    className={`p-2 rounded-lg flex items-center gap-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                  >
                    {isMobile ? (
                      <>
                        <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Back</span>
                      </>
                    ) : (
                      <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                    )}
                  </button>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Schedule Post
                  </h2>
                  <div className="w-9" />
                </div>
              </div>

              <div className="p-4 space-y-6">
                {/* Image Preview */}
                <div className="relative rounded-2xl overflow-hidden">
                  {postDraft.imageUrl ? (
                    <img
                      src={postDraft.imageUrl}
                      alt="Post preview"
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className={`w-full aspect-square flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                      <ImageIcon className={`w-16 h-16 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                    </div>
                  )}
                  {/* Regenerate Image Button */}
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                    <button
                      onClick={handleRegenerateImage}
                      disabled={isLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/90 backdrop-blur-sm text-slate-900 font-medium hover:bg-white transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      Regenerate image
                    </button>
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/90 backdrop-blur-sm text-slate-900 font-medium hover:bg-white transition-colors"
                    >
                      <Sparkles className="w-4 h-4" />
                      AI Edit
                    </button>
                  </div>
                </div>

                {/* Content Section */}
                <div className={`rounded-xl border p-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                    Content
                  </h3>
                  <textarea
                    value={postDraft.content}
                    onChange={(e) => setPostDraft(prev => ({ ...prev, content: e.target.value }))}
                    rows={4}
                    className={`w-full bg-transparent resize-none focus:outline-none ${isDark ? 'text-white' : 'text-slate-900'}`}
                    placeholder="Write your caption..."
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleRegenerateCaption}
                      disabled={isLoading}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isDark
                          ? 'bg-white/10 text-white hover:bg-white/20'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      } disabled:opacity-50`}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                      Regenerate caption
                    </button>
                    <button
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isDark
                          ? 'bg-white/10 text-white hover:bg-white/20'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      AI Edit
                    </button>
                  </div>
                </div>

                {/* Publish Date */}
                <div className={`rounded-xl border p-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Publish date
                      </h3>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {postDraft.publishDate
                          ? new Date(`${postDraft.publishDate}T${postDraft.publishTime}`).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            }) + ' at ' + new Date(`${postDraft.publishDate}T${postDraft.publishTime}`).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : 'Select date'}
                      </p>
                    </div>
                    <Calendar className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <input
                      type="date"
                      value={postDraft.publishDate}
                      onChange={(e) => setPostDraft(prev => ({ ...prev, publishDate: e.target.value }))}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white'
                          : 'bg-slate-50 border-slate-200 text-slate-900'
                      }`}
                    />
                    <input
                      type="time"
                      value={postDraft.publishTime}
                      onChange={(e) => setPostDraft(prev => ({ ...prev, publishTime: e.target.value }))}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white'
                          : 'bg-slate-50 border-slate-200 text-slate-900'
                      }`}
                    />
                  </div>
                </div>

                {/* Content Type */}
                <div className={`rounded-xl border p-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Content type
                      </h3>
                      <p className={`font-medium capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {postDraft.contentType}
                      </p>
                    </div>
                    <ChevronDown className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </div>
                  <select
                    value={postDraft.contentType}
                    onChange={(e) => setPostDraft(prev => ({ ...prev, contentType: e.target.value as 'post' | 'story' | 'reel' }))}
                    className={`w-full mt-3 px-3 py-2 rounded-lg border text-sm ${
                      isDark
                        ? 'bg-dark-700 border-white/10 text-white'
                        : 'bg-slate-50 border-slate-200 text-slate-900'
                    }`}
                  >
                    <option value="post">Post</option>
                    <option value="story">Story</option>
                    <option value="reel">Reel</option>
                  </select>
                </div>

                {/* Platform */}
                <div className={`rounded-xl border p-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Platform
                      </h3>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded flex items-center justify-center text-white ${getProviderColor(postDraft.platform)}`}>
                          {getProviderIcon(postDraft.platform)}
                        </div>
                        <p className={`font-medium capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {postDraft.platform}
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                  </div>
                  <select
                    value={postDraft.platform}
                    onChange={(e) => setPostDraft(prev => ({ ...prev, platform: e.target.value }))}
                    className={`w-full mt-3 px-3 py-2 rounded-lg border text-sm ${
                      isDark
                        ? 'bg-dark-700 border-white/10 text-white'
                        : 'bg-slate-50 border-slate-200 text-slate-900'
                    }`}
                  >
                    {connectedIntegrations.length > 0 ? (
                      connectedIntegrations.map(integration => (
                        <option key={integration.id} value={integration.provider}>
                          {integration.provider_name || integration.provider}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="twitter">Twitter / X</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Schedule Button */}
                <button
                  onClick={handleSchedulePost}
                  disabled={isSchedulingPost || !postDraft.content || !postDraft.publishDate}
                  className="w-full py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSchedulingPost ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Schedule
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Connector Edit Modal */}
      <ConnectorEditModal
        isOpen={isEditModalOpen}
        action={editingAction}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAction(null);
        }}
        onSave={handleSaveEditedAction}
        isLoading={actionLoadingId === editingAction?.id}
      />

      {/* More Prompts Modal */}
      <AnimatePresence>
        {showMorePromptsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowMorePromptsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`relative w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden ${
                isDark ? 'bg-dark-900 border border-white/10' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                    <span className="text-lg">{employee.avatar}</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}'s Capabilities
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Click any prompt to get started
                    </p>
                  </div>
                </div>
                <motion.button
                  onClick={() => setShowMorePromptsModal(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {employee.capabilities.map((capability, index) => {
                    const details = CAPABILITY_DETAILS[capability];
                    const title = details?.title || capability;
                    const description = details?.description || `Help me with ${capability.toLowerCase()}`;
                    const prompt = details?.prompt || `Help me with ${capability.toLowerCase()}`;

                    return (
                      <motion.button
                        key={capability}
                        onClick={() => {
                          handleUseCaseClick(prompt);
                          setShowMorePromptsModal(false);
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          isDark
                            ? 'bg-dark-800/50 border-white/10 hover:border-purple-500/30 hover:bg-dark-800'
                            : 'bg-slate-50 border-slate-200 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                      >
                        <div>
                          <span className={`text-sm font-medium block ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
                            {title}
                          </span>
                          <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            {description}
                          </p>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bot Settings Modal */}
      <AnimatePresence>
        {showBotSettingsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowBotSettingsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${
                isDark ? 'bg-dark-900 border border-white/10' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}>
                    <span className="text-lg">{employee.avatar}</span>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name} Settings
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Customize how {employee.name} responds
                    </p>
                  </div>
                </div>
                <motion.button
                  onClick={() => setShowBotSettingsModal(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-500'
                  }`}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-5">
                {isLoadingBotConfig ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                  </div>
                ) : (
                  <>
                    {/* Tone Selection */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                        Response Tone
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {['professional', 'casual', 'friendly', 'formal'].map((tone) => (
                          <button
                            key={tone}
                            onClick={() => setBotConfig(prev => ({ ...prev, tone }))}
                            className={`px-4 py-2.5 rounded-xl border text-sm font-medium capitalize transition-all ${
                              botConfig.tone === tone
                                ? 'bg-purple-500 border-purple-500 text-white'
                                : isDark
                                  ? 'bg-dark-800 border-white/10 text-gray-300 hover:border-purple-500/50'
                                  : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-purple-300'
                            }`}
                          >
                            {tone}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Instructions */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                        Custom Instructions
                      </label>
                      <textarea
                        value={botConfig.custom_instructions}
                        onChange={(e) => setBotConfig(prev => ({ ...prev, custom_instructions: e.target.value }))}
                        placeholder={`Add specific instructions for ${employee.name}. For example:\n- Always include examples\n- Use bullet points\n- Keep responses concise`}
                        rows={5}
                        className={`w-full px-4 py-3 rounded-xl border text-sm resize-none transition-colors ${
                          isDark
                            ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-400'
                        } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                      />
                      <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        These instructions will be applied to all conversations with {employee.name} in this workspace.
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <button
                  onClick={() => setShowBotSettingsModal(false)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    isDark
                      ? 'text-gray-300 hover:bg-white/10'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveBotConfig}
                  disabled={isSavingBotConfig || isLoadingBotConfig}
                  className="px-4 py-2 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSavingBotConfig && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Settings
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
